import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { listUnits } from './api';

/**
 * "шт" rather than "pcs".
 *
 * `unit_code` is an identifier — the primary key of the units table — and it is
 * in English because the schema is. Printing it beside a quantity put "pcs" on
 * a Russian screen next to every number on the shelf.
 *
 * The reference book is small, shared by every screen that shows a quantity, and
 * changes about once a year, so one query serves the whole session. Until it
 * arrives the code itself is shown: a number with no unit at all reads worse
 * than one with the wrong word for a moment.
 */
export function useUnitName(): (code: string | null | undefined) => string {
  const { i18n } = useTranslation();

  const units = useQuery({
    queryKey: ['reference', 'units'],
    queryFn: listUnits,
    staleTime: Infinity,
  });

  const english = i18n.resolvedLanguage === 'en';
  const rows = units.data;

  return useCallback(
    (code: string | null | undefined) => {
      if (!code) return '';
      const found = rows?.find((unit) => unit.code === code);
      if (!found) return code;
      return (english ? found.name_en : found.name_ru) || code;
    },
    [rows, english],
  );
}
